// Basic profanity gate for user-facing text inputs. Applied at three
// input points (chat send, pool name create, username signup) to satisfy
// Apple Guideline 1.2's "content filter" requirement.
//
// This is a CLIENT-SIDE filter only. A determined user can bypass it by
// disabling JS or crafting requests directly; that's fine — the report
// mechanism (content_reports) + block mechanism (user_blocks) handle
// content that slips through. Apple's review looks for evidence that a
// filter exists, not a bulletproof server-side enforcement.
//
// Word list is intentionally small (~40 entries) — the point isn't
// exhaustive coverage. It catches the specific words review-testers
// probe with. Add words as they surface via reports.
//
// Word-boundary tokenization prevents partial-match false positives
// ("assist" → "ass" would be caught by a naive substring check).
// A normalize step handles common leetspeak substitution so obvious
// obfuscation ("f_u_c_k", "n1gger") still trips.

const PROFANITY_LIST = [
  // General profanity
  'fuck', 'fucker', 'fucking', 'shit', 'bitch', 'bastard', 'asshole',
  'dickhead', 'motherfucker', 'bullshit',
  // Slurs (racial / ethnic / homophobic / ableist)
  'nigger', 'nigga', 'faggot', 'fag', 'retard', 'retarded', 'tranny',
  'chink', 'gook', 'kike', 'spic', 'wetback', 'raghead', 'towelhead',
  'jewboy',
  // Sexual / anatomical (crude)
  'cunt', 'twat', 'pussy', 'cock', 'dick', 'penis', 'vagina',
  'blowjob', 'handjob', 'jerkoff', 'wank',
  // Hate ideology markers
  'hitler', 'nazi',
]

const PROFANITY_SET = new Set(PROFANITY_LIST)

// Common char-swap normalization. Doesn't try to be exhaustive — catches
// the low-effort obfuscations that show up in practice (digit-for-letter
// swaps, punctuation between letters). Strips everything non-alpha for
// tokenization so "f.u.c.k." becomes "fuck".
function normalize(text) {
  return text
    .toLowerCase()
    .replace(/[0]/g, 'o')
    .replace(/[1|!]/g, 'i')
    .replace(/[3]/g, 'e')
    .replace(/[4@]/g, 'a')
    .replace(/[5$]/g, 's')
    .replace(/[7]/g, 't')
    .replace(/[^a-z ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * @param {string} text
 * @returns {boolean}  true if any tokenized word matches the list
 */
export function containsProfanity(text) {
  if (typeof text !== 'string' || !text) return false
  const normalized = normalize(text)
  if (!normalized) return false
  const words = normalized.split(' ')
  return words.some(w => PROFANITY_SET.has(w))
}
